/**
 * TJR's per-block Summary ticks (N32, TJR inventory 02 #67; docs/specs/project-reporting/overview.md §9):
 * "Include block data in report" and "Job km's are completed".
 *
 * - Included (the default): the block's line listing is printed in the daily progress report. Unticked, it is left
 *   out (the Job Summary's totals still count the block).
 * - Completed: every km of the block has been flown, so the lines of the block left to fly count 0 km remaining in
 *   the estimates (the Project overview, the Job Summary's Est KM Remaining and Est Days To Complete). TJR asks
 *   "Are you sure all km for this block have been flown?" and refuses a block with no km ("Error: Zero KM found in
 *   block:"); Teslyn asks the same and refuses a block with no accepted km.
 */
import type { Block, LineStatus, PlannedLine } from './model.js';
import type { PlanProgress } from './progress.js';

type Flags = Pick<Block, 'report_include' | 'km_completed'>;

/** Whether the block's data goes in the report (TJR's default: yes). */
export function blockInReport(b: Flags): boolean {
  return b.report_include !== false;
}

/** Whether the block's km are all flown ("Job km's are completed"). */
export function blockKmCompleted(b: Flags): boolean {
  return b.km_completed === true;
}

/** The question asked before a block is marked completed (TJR's words, sentence case). */
export function kmCompletedQuestion(b: Pick<Block, 'name'>): string {
  return `Are you sure all km for block ${b.name} have been flown?`;
}

/**
 * Why a block cannot be marked completed, or null: a block with no accepted km (TJR: "Error: Zero KM found in
 * block:"). The block's lines are its planned lines' statuses that are reviewed and use their km.
 */
export function kmCompletedRefusal(
  b: Pick<Block, 'id' | 'name'>,
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
): string | null {
  const mine = new Set(plan.filter((p) => p.block_id === b.id).map((p) => p.id));
  const own = statuses.filter((s) => s.deleted_at === null && mine.has(s.planned_line_id));
  // the reportTotals rule (ADR-013): reviewed, use_km, accepted_m
  const accepted = own
    .filter((s) => s.acceptance !== 'pending' && s.use_km)
    .reduce((a, s) => a + s.accepted_m, 0);
  return accepted > 0 ? null : `No accepted km found in block ${b.name}: it cannot be marked completed.`;
}

/**
 * `planProgress` with the lines of completed blocks counting 0 km remaining (their state is kept). Blocks not given,
 * or none completed: the progress as it is.
 */
export function withCompletedBlocks(
  progress: PlanProgress,
  blocks: readonly Pick<Block, 'id' | 'km_completed'>[] = [],
): PlanProgress {
  const done = new Set(blocks.filter(blockKmCompleted).map((b) => b.id));
  if (done.size === 0) return progress;
  const lines = progress.lines.map((l) => (done.has(l.original.block_id) ? { ...l, remaining_m: 0 } : l));
  return { ...progress, lines, remainingM: lines.reduce((a, l) => a + l.remaining_m, 0) };
}

/** Who may change the two ticks: the block's writers in Teslyn Cloud (the block policies, office staff included). */
export const BLOCK_FLAG_EDITORS = ['tenant_admin', 'manager', 'office_staff'] as const;

/**
 * Whether the signed-in person may change a block's ticks: a local workspace always; signed in, a tenant admin,
 * manager or office staff member; roles not known yet, no.
 */
export function blockFlagsEditable(roles: readonly string[] | null, localWorkspace: boolean): boolean {
  if (localWorkspace) return true;
  return roles !== null && roles.some((r) => (BLOCK_FLAG_EDITORS as readonly string[]).includes(r));
}
